import { useState, useEffect, useCallback, memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import lobbyLounge from "@/assets/lobby-lounge.webp";

// Lazy load other images only when needed
const lazyImages = [
  () => import("@/assets/room-overview.webp"),
  () => import("@/assets/lobby-reception.webp"),
  () => import("@/assets/room-kitchen.webp"),
  () => import("@/assets/hallway.webp"),
  () => import("@/assets/room-bedroom.webp"),
  () => import("@/assets/room-kitchen-2.webp"),
  () => import("@/assets/room-bathroom.webp"),
];

const imageData = [
  { alt: "Harbor Heights lobby lounge area with comfortable seating in Sioux City Iowa" },
  { alt: "Extended stay suite room overview showing bed, kitchenette and workspace" },
  { alt: "Hotel reception area with 24/7 front desk service" },
  { alt: "Full kitchenette with microwave, refrigerator and sink in extended stay room" },
  { alt: "Clean and well-lit hotel hallway at Harbor Heights" },
  { alt: "Comfortable bedroom with full-size bed and quality linens" },
  { alt: "Kitchen area with ample cabinet storage for extended stays" },
  { alt: "Modern private bathroom with walk-in shower" },
];

const HeroSlideshow = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [loadedImages, setLoadedImages] = useState<string[]>([lobbyLounge]);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile on mount
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Preload next image when current changes
  useEffect(() => {
    const nextIndex = (currentIndex + 1) % imageData.length;
    if (nextIndex > 0 && !loadedImages[nextIndex]) {
      lazyImages[nextIndex - 1]().then((module) => {
        setLoadedImages((prev) => {
          const newImages = [...prev];
          newImages[nextIndex] = module.default;
          return newImages;
        });
      });
    }
  }, [currentIndex, loadedImages]);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % imageData.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + imageData.length) % imageData.length);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    
    // Slower interval on mobile to reduce repaints
    const interval = setInterval(() => {
      nextSlide();
    }, isMobile ? 6000 : 5000);

    return () => clearInterval(interval);
  }, [isPaused, nextSlide, isMobile]);

  const handleManualNav = (action: () => void) => {
    action();
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 10000);
  };

  const currentImage = loadedImages[currentIndex] || lobbyLounge;

  // Simplified animations for mobile
  const mobileVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const desktopVariants = {
    initial: { opacity: 0, scale: 1.1 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0 },
  };

  const mobileTransition = {
    opacity: { duration: 0.8, ease: "easeInOut" as const },
  };

  const desktopTransition = {
    opacity: { duration: 1.5, ease: "easeInOut" as const },
    scale: { duration: 8, ease: "easeOut" as const },
  };

  return (
    <div className="absolute inset-0 overflow-hidden group" role="region" aria-label="Property image slideshow" aria-roledescription="carousel">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          variants={isMobile ? mobileVariants : desktopVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={isMobile ? mobileTransition : desktopTransition}
          className="absolute inset-0"
          aria-hidden="true"
        >
          <img
            src={currentImage}
            alt={imageData[currentIndex].alt}
            width={1920}
            height={1080}
            className="w-full h-full object-cover"
            loading={currentIndex === 0 ? "eager" : "lazy"}
            fetchPriority={currentIndex === 0 ? "high" : "auto"}
            decoding={currentIndex === 0 ? "sync" : "async"}
          />
        </motion.div>
      </AnimatePresence>
      
      {/* Blue overlay gradient */}
      <div className="absolute inset-0 gradient-hero" />

      {/* Navigation Arrows - Hidden on mobile by default, visible on hover for desktop */}
      <button
        onClick={() => handleManualNav(prevSlide)}
        className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-opacity duration-300 opacity-0 group-hover:opacity-100 md:transition-all"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true" />
      </button>
      <button
        onClick={() => handleManualNav(nextSlide)}
        className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-opacity duration-300 opacity-0 group-hover:opacity-100 md:transition-all"
        aria-label="Next slide"
      >
        <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true" />
      </button>
      
    </div>
  );
};

export default memo(HeroSlideshow);